import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import PageHeader from '../../components/PageHeader/PageHeader';
import StatCard from '../../components/StatCard/StatCard';
import EmptyState from '../../components/EmptyState/EmptyState';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import SwitchInput from '../../components/SwitchInput/SwitchInput';
import AIInsights from '../Dashboard/AIInsights';
import SentimentWidget from '../Dashboard/SentimentWidget';
import LineChart from '../Reports/LineChart';
import { getAutoBot, updateAutoBot, startAutoBot, stopAutoBot } from '../../services/AutoBotService';
import { getPerformance } from '../../services/AnalyticsService';
import { getActiveTargets } from '../../services/ExchangeService';
import { fmtMoney, fmtPct, fmtPrice } from '../../utils/format';
import logger from '../../utils/logger';

const POLL_MS = 6000;

const RISK_LEVELS = ['SAFE', 'MEDIUM', 'RISKY'];
const LEVERAGES = [2, 5, 10];
const BUDGET_PRESETS = [10, 15, 20];

// 'AGGRESSIVE' is a legacy alias kept server-side; the UI only knows 'RISKY'.
function normalizeConfig(config) {
    if (!config) return config;
    // DECIMAL columns arrive as strings ("10.00000000") — coerce so number inputs
    // show clean values and edits compare correctly.
    const c = {
        ...config,
        budgetPerTrade: config.budgetPerTrade != null ? parseFloat(config.budgetPerTrade) : config.budgetPerTrade,
        maxTradesPerDay: config.maxTradesPerDay != null ? parseInt(config.maxTradesPerDay, 10) : config.maxTradesPerDay,
        maxConcurrent: config.maxConcurrent != null ? parseInt(config.maxConcurrent, 10) : config.maxConcurrent,
        leverage: config.leverage != null ? parseInt(config.leverage, 10) : config.leverage
    };
    if (c.riskLevel === 'AGGRESSIVE') c.riskLevel = 'RISKY';
    return c;
}

// Map an engine code to a colored badge class.
const ENGINE_BADGE = {
    TODAY: 'gh-badge-success',
    RISK: 'gh-badge-gold',
    FUTURES: 'gh-badge-danger'
};

function AutoBot() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const tv = (id, values) => formatMessage({ id }, values);

    const [data, setData] = useState(null);          // { config, managed, planned, freeBalance }
    const [cfg, setCfg] = useState(null);            // editable copy of config
    const [perf, setPerf] = useState(null);          // AnalyticsService performance
    const [targets, setTargets] = useState([]);      // live active targets (P&L)
    const [confirmLive, setConfirmLive] = useState(false); // master ON in LIVE → confirm
    const [busy, setBusy] = useState(false);
    const [notification, setNotification] = useState({});

    // Poll the bot state + live targets while mounted. Config is only synced from
    // the server when the user is NOT mid-edit (no local changes pending) to avoid
    // clobbering in-progress edits.
    const refresh = useCallback((syncCfg) => {
        getAutoBot()
            .then(d => {
                setData(d);
                if (syncCfg && d && d.config) setCfg(prev => prev ? prev : normalizeConfig({ ...d.config }));
            })
            .catch(err => logger.error(err.response ? err.response.data : err.message));
        getActiveTargets()
            .then(r => setTargets(Array.isArray(r) ? r : []))
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }, []);

    useEffect(() => {
        // Initial load syncs the editable config; polling does not.
        refresh(true);
        const timer = setInterval(() => refresh(false), POLL_MS);
        return () => clearInterval(timer);
    }, [refresh]);

    // LIVE-only platform (owner decision 2026-07-29): mode is always LIVE.
    const mode = 'LIVE';
    useEffect(() => {
        getPerformance(mode || 'ALL')
            .then(setPerf)
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }, [mode]);

    const config = data ? data.config : null;
    const managed = (data && data.managed) || [];
    const planned = (data && data.planned) || [];
    const freeBalance = data ? data.freeBalance : null;
    const regime = (data && data.regime) || null;          // { regime, reasons, details }
    const macro = (data && data.macro) || null;            // { fearGreed, newsTone }
    const capital = (data && data.capital) || null;        // { equity, deployed, free, nextBudget, guard }
    const vetoes = (data && data.vetoes) || [];            // [{ symbol, reason, at }]
    const bnb = (data && data.bnb) || null;                // { fund, bnbValue, share, target, action, at }

    // Regime → badge color. TREND_DOWN is caution (gold), CRASH/UNKNOWN are hard red.
    const REGIME_BADGE = {
        RANGE: 'gh-badge-info', TREND_UP: 'gh-badge-success',
        TREND_DOWN: 'gh-badge-gold', CRASH: 'gh-badge-danger', UNKNOWN: 'gh-badge-danger'
    };
    const baseCurrency = (cfg && cfg.baseCurrency) || (config && config.baseCurrency) || 'USDC';
    const enabled = config ? !!config.enabled : false;

    const num = (v) => parseFloat(v) || 0;
    // Open positions count with their honest "net if sold now" (fees on both
    // sides already subtracted, from the backend); flat sessions with realized P&L.
    const openPnl = managed.reduce((a, m) => a + (num(m.openQty) > 0 && m.netIfSellNow != null ? num(m.netIfSellNow) : num(m.pnl)), 0);

    function setField(patch) {
        setCfg(prev => ({ ...(prev || {}), ...patch }));
    }

    function onCfgChange(event) {
        const { id, value } = event.target;
        setField({ [id]: value });
    }

    // Step an integer field by delta, clamped to [min, max]. Used by the −/+ buttons.
    function stepField(id, delta, min, max) {
        setCfg(prev => {
            const cur = parseInt((prev || {})[id], 10);
            const base = Number.isFinite(cur) ? cur : 0;
            let next = base + delta;
            if (next < min) next = min;
            if (next > max) next = max;
            return { ...(prev || {}), [id]: next };
        });
    }

    // ----- Master ON/OFF -----
    function onMasterToggle() {
        if (enabled) {
            doStop();
            return;
        }
        // Turning ON. LIVE needs an explicit real-money confirmation.
        if (mode === 'LIVE') { setConfirmLive(true); return; }
        doStart();
    }

    function doStart() {
        setConfirmLive(false);
        setBusy(true);
        startAutoBot()
            .then(() => { setNotification({ type: 'success', text: t('autobot.started') }); refresh(false); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }))
            .finally(() => setBusy(false));
    }

    function doStop() {
        setBusy(true);
        stopAutoBot()
            .then(() => { setNotification({ type: 'success', text: t('autobot.stopped') }); refresh(false); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }))
            .finally(() => setBusy(false));
    }

    // ----- Save config (PATCH) -----
    function onSave() {
        if (!cfg) return;
        const payload = {
            mode: cfg.mode,
            budgetPerTrade: parseFloat(cfg.budgetPerTrade) || 0,
            maxTradesPerDay: parseInt(cfg.maxTradesPerDay, 10) || 0,
            maxConcurrent: parseInt(cfg.maxConcurrent, 10) || 0,
            useFutures: !!cfg.useFutures,
            riskLevel: cfg.riskLevel,
            leverage: parseInt(cfg.leverage, 10) || 2
        };
        setBusy(true);
        updateAutoBot(payload)
            .then(updated => {
                const norm = normalizeConfig(updated);
                setNotification({ type: 'success', text: t('autobot.saved') });
                setData(prev => prev ? { ...prev, config: { ...prev.config, ...norm } } : prev);
                setCfg(prev => ({ ...prev, ...norm }));
            })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }))
            .finally(() => setBusy(false));
    }

    // Build the equity curve into the shape LineChart expects.
    const equity = (perf && perf.equityCurve) || [];
    const lineData = {
        quote: baseCurrency,
        profit: perf && perf.overall ? num(perf.overall.realizedPnl) : 0,
        profitPerc: 0,
        sellVolume: perf && perf.overall ? num(perf.overall.realizedPnl) : 0,
        buyVolume: 0,
        series: equity.map(p => num(p.cumulative)),
        subs: equity.map(p => (p.date ? String(p.date).slice(5) : ''))
    };

    const overall = (perf && perf.overall) || {};
    const fmtSigned = (v) => `${v >= 0 ? '+' : ''}${fmtMoney(v)}`;

    if (!data) {
        return (
            <React.Fragment>
                <Menu />
                <main className="content">
                    <PageHeader title={t('autobot.title')} subtitle={t('autobot.subtitle')} />
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                    <Footer />
                </main>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <PageHeader title={t('autobot.title')} subtitle={t('autobot.subtitle')}>
                    <label
                        className={`gh-master-switch ${enabled ? 'on' : ''}`}
                        htmlFor="autobot-master"
                        title={t('autobot.masterSwitch')}
                    >
                        <span className={`gh-dot ${enabled ? 'gh-dot-warning' : 'gh-dot-muted'}`}></span>
                        <strong className={enabled ? '' : 'text-muted'} style={enabled ? { color: 'var(--gh-gold)' } : undefined}>
                            {enabled ? t('autobot.on') : t('autobot.off')}
                        </strong>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="autobot-master"
                                aria-label={t('autobot.masterSwitch')}
                                checked={enabled}
                                disabled={busy}
                                onChange={onMasterToggle}
                            />
                        </div>
                    </label>
                </PageHeader>

                {config && config.lastMessage ? (
                    <div className="alert alert-secondary py-2 mb-3">
                        <small className="text-muted">{config.lastMessage}</small>
                    </div>
                ) : null}

                {/* ---------- MARKET REGIME BANNER ---------- */}
                {regime ? (
                    <div className="card card-body border-0 shadow mb-4 py-3">
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            <span className={`gh-badge ${REGIME_BADGE[regime.regime] || 'gh-badge-info'}`} style={{ fontSize: '0.95em' }}>
                                {t(`autobot.regime.${regime.regime}`) || regime.regime}
                            </span>
                            <small className="text-muted">
                                {regime.details && regime.details.btc7d != null ? <span className="me-3">BTC 7d <span className={`num ${regime.details.btc7d >= 0 ? 'gh-pos' : 'gh-neg'}`}>{regime.details.btc7d}%</span></span> : null}
                                {regime.details && regime.details.breadth != null ? <span className="me-3">{t('autobot.market.breadth')} <span className="num">{regime.details.breadth}%</span></span> : null}
                                {macro && macro.fearGreed != null ? <span className="me-3">{t('autobot.market.fearGreed')} <span className="num">{macro.fearGreed}</span></span> : null}
                                {macro && macro.newsTone != null ? <span>{t('autobot.market.newsTone')} <span className={`num ${macro.newsTone >= 0 ? 'gh-pos' : 'gh-neg'}`}>{macro.newsTone >= 0 ? '+' : ''}{macro.newsTone}</span></span> : null}
                            </small>
                        </div>
                        {regime.reasons && regime.reasons.length ? (
                            <small className="text-muted mt-1">{t(`autobot.regimeHint.${regime.regime}`)} · {regime.reasons[0]}</small>
                        ) : null}
                    </div>
                ) : null}

                {/* ---------- STATUS STRIP ---------- */}
                <div className="gh-grid mb-4">
                    <div>
                        <StatCard
                            label={t('autobot.status')}
                            value={
                                <span className="d-inline-flex align-items-center">
                                    <span className={`gh-dot ${enabled ? 'gh-dot-warning' : 'gh-dot-muted'} me-2`}></span>
                                    {enabled ? t('autobot.on') : t('autobot.off')}
                                </span>
                            }
                            gold={enabled}
                            sub={
                                <span className={`gh-badge ${mode === 'LIVE' ? 'gh-badge-danger' : 'gh-badge-info'}`}>
                                    {mode === 'LIVE' ? t('autobot.live') : t('autobot.paper')}
                                </span>
                            }
                        />
                    </div>
                    <div>
                        <StatCard
                            label={t('autobot.tradesToday')}
                            value={<span className="num">{config ? config.tradesToday : 0} / {config ? config.maxTradesPerDay : 0}</span>}
                            sub={t('autobot.tradesTodayHint')}
                        />
                    </div>
                    <div>
                        <StatCard
                            label={t('autobot.openPnl')}
                            value={<span className={`num ${openPnl >= 0 ? 'gh-pos' : 'gh-neg'}`}>{fmtSigned(openPnl)}</span>}
                            sub={tv('autobot.openPositionsCount', { n: managed.length })}
                        />
                    </div>
                    <div>
                        <StatCard
                            label={tv('autobot.freeBalance', { ccy: baseCurrency })}
                            value={<span className="num">{fmtMoney(freeBalance)}</span>}
                            gold
                        />
                    </div>
                </div>

                {/* ---------- CAPITAL MANAGER (LIVE) ---------- */}
                {capital ? (
                    <div className="gh-grid mb-4">
                        <div>
                            <StatCard
                                label={tv('autobot.capital.equity', { ccy: baseCurrency })}
                                value={<span className="num">{fmtMoney(capital.equity)}</span>}
                                gold
                                sub={t('autobot.capital.equityHint')}
                            />
                        </div>
                        <div>
                            <StatCard
                                label={t('autobot.capital.deployed')}
                                value={<span className="num">{fmtMoney(capital.deployed)}</span>}
                                sub={tv('autobot.capital.ofEquity', { pct: capital.equity > 0 ? fmtPct((capital.deployed / capital.equity) * 100) : '0' })}
                            />
                        </div>
                        <div>
                            <StatCard
                                label={t('autobot.capital.nextBudget')}
                                value={<span className="num">{fmtMoney(capital.nextBudget)}</span>}
                                sub={t('autobot.capital.nextBudgetHint')}
                            />
                        </div>
                        <div>
                            <StatCard
                                label={t('autobot.capital.guard')}
                                value={
                                    <span className={`d-inline-flex align-items-center ${capital.guard && capital.guard.blocked ? 'gh-neg' : ''}`}>
                                        <span className={`gh-dot ${capital.guard && capital.guard.blocked ? 'gh-dot-danger' : 'gh-dot-success'} me-2`}></span>
                                        {capital.guard && capital.guard.blocked ? t('autobot.capital.guardBlocked') : t('autobot.capital.guardOk')}
                                    </span>
                                }
                                sub={capital.guard ? tv('autobot.capital.guardSub', { drop: capital.guard.dropPct, limit: capital.guard.limitPct }) : null}
                            />
                        </div>
                    </div>
                ) : null}

                {/* ---------- BNB FEE RESERVE ---------- */}
                {bnb && bnb.enabled ? (
                    <div className="card card-body border-0 shadow mb-4 py-3">
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            <small className="text-muted text-uppercase">{t('autobot.bnb.title')}</small>
                            <span className="num">
                                BNB ≈ {fmtMoney(bnb.bnbValue != null ? bnb.bnbValue : 0)} {baseCurrency}
                            </span>
                            <span className={`gh-badge ${bnb.bnbValue != null && bnb.bnbValue >= bnb.target ? 'gh-badge-success' : 'gh-badge-gold'}`}>
                                {tv('autobot.bnb.fund', { amount: fmtMoney(bnb.fund), ccy: baseCurrency })}
                            </span>
                            <small className="text-muted">
                                {tv('autobot.bnb.share', { pct: Math.round((bnb.share || 0.2) * 100), target: bnb.target, ccy: baseCurrency })}
                            </small>
                            {bnb.action === 'bought' && bnb.at ? (
                                <small className="gh-pos">{tv('autobot.bnb.lastBuy', { at: new Date(bnb.at).toLocaleString() })}</small>
                            ) : (
                                <small className="text-muted">{t('autobot.bnb.waiting')}</small>
                            )}
                        </div>
                    </div>
                ) : null}

                {/* ---------- CONFIG ---------- */}
                {cfg ? (
                    <div className="card card-body border-0 shadow mb-4">
                        <small className="text-muted text-uppercase d-block mb-1">{t('autobot.configTitle')}</small>
                        <p className="small text-muted mb-3">{t('autobot.recommended')}</p>
                        <div className="gh-grid gh-grid-wide">
                            <div>
                                <label className="form-label" htmlFor="autobot-ccy">{t('autobot.baseCurrency')}</label>
                                <select id="autobot-ccy" className="form-select" value={baseCurrency} disabled>
                                    <option value="USDC">USDC</option>
                                </select>
                            </div>
                            <div>
                                <label className="form-label" htmlFor="budgetPerTrade">{tv('autobot.budgetPerTrade', { ccy: baseCurrency })}</label>
                                <input
                                    id="budgetPerTrade"
                                    type="number"
                                    min="0"
                                    step="1"
                                    className="form-control"
                                    value={cfg.budgetPerTrade != null ? cfg.budgetPerTrade : ''}
                                    onChange={onCfgChange}
                                />
                                <div className="d-flex gap-1 mt-1">
                                    {BUDGET_PRESETS.map(v => (
                                        <button
                                            key={v}
                                            type="button"
                                            className={`btn btn-sm ${num(cfg.budgetPerTrade) === v ? 'btn-primary' : 'btn-outline-gray'}`}
                                            onClick={() => setField({ budgetPerTrade: v })}
                                        >
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="form-label" htmlFor="maxTradesPerDay">{t('autobot.maxTradesPerDay')}</label>
                                <div className="input-group">
                                    <button type="button" className="btn btn-outline-gray" title={t('autobot.dec')} onClick={() => stepField('maxTradesPerDay', -1, 0, 500)}>−</button>
                                    <input
                                        id="maxTradesPerDay"
                                        type="number"
                                        min="0"
                                        max="500"
                                        step="1"
                                        className="form-control text-center"
                                        value={cfg.maxTradesPerDay != null ? cfg.maxTradesPerDay : ''}
                                        onChange={onCfgChange}
                                    />
                                    <button type="button" className="btn btn-outline-gray" title={t('autobot.inc')} onClick={() => stepField('maxTradesPerDay', 1, 0, 500)}>+</button>
                                </div>
                                <small className="text-muted">{t('autobot.maxTradesPerDayHint')}</small>
                            </div>
                            <div>
                                <label className="form-label" htmlFor="maxConcurrent">{t('autobot.maxConcurrent')}</label>
                                <input
                                    id="maxConcurrent"
                                    type="number"
                                    min="1"
                                    step="1"
                                    className="form-control"
                                    value={cfg.maxConcurrent != null ? cfg.maxConcurrent : ''}
                                    onChange={onCfgChange}
                                />
                                <small className="text-muted">{t('autobot.maxConcurrentHint')}</small>
                            </div>
                        </div>

                        <hr className="my-4" />

                        <div className="gh-grid gh-grid-wide align-items-end">
                            <div>
                                <label className="form-label" htmlFor="riskLevel">{t('autobot.riskLevel')}</label>
                                <select id="riskLevel" className="form-select" value={cfg.riskLevel || 'SAFE'} onChange={onCfgChange}>
                                    {RISK_LEVELS.map(r => (
                                        <option key={r} value={r}>{t(`autobot.riskLevel.${r}`)}</option>
                                    ))}
                                </select>
                                <p className="small text-muted mt-1 mb-0">{t(`autobot.modeDesc.${cfg.riskLevel || 'SAFE'}`)}</p>
                                {cfg.riskLevel === 'RISKY' && localStorage.getItem('hasFutures') === 'true' ? (
                                    <div className="mt-3">
                                        <SwitchInput
                                            id="useFutures"
                                            text={t('autobot.futures')}
                                            isChecked={!!cfg.useFutures}
                                            onChange={onCfgChange}
                                        />
                                        <small className="text-muted d-block">{t('autobot.futuresHint')}</small>
                                        {cfg.useFutures ? (
                                            <div className="mt-2">
                                                <label className="form-label" htmlFor="leverage">{t('autobot.leverage')}</label>
                                                <select id="leverage" className="form-select" value={cfg.leverage || 2} onChange={onCfgChange}>
                                                    {LEVERAGES.map(l => <option key={l} value={l}>{l}x</option>)}
                                                </select>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                            <div>
                                <label className="form-label d-block">{t('autobot.mode')}</label>
                                <span className="gh-badge gh-badge-danger">{t('autobot.live')}</span>
                                <small className="text-danger d-block mt-1">{t('autobot.liveWarn')}</small>
                            </div>
                            <div className="text-xl-end">
                                <button type="button" className="btn btn-primary" disabled={busy} onClick={onSave}>
                                    {t('autobot.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* ---------- WHAT IT'S DOING NOW ---------- */}
                <div className="card card-body border-0 shadow mb-4">
                    <small className="text-muted text-uppercase d-block mb-3">{t('autobot.doingNow')}</small>
                    {managed.length === 0 ? (
                        <EmptyState title={t('autobot.doingNow.empty')} text={t('autobot.doingNow.emptyHint')} />
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-middle mb-0 gh-table-stack">
                                <thead>
                                    <tr>
                                        <th>{t('autobot.col.engine')}</th>
                                        <th>{t('autobot.col.symbol')}</th>
                                        <th className="num">{t('autobot.col.entry')}</th>
                                        <th className="num">{t('autobot.col.current')}</th>
                                        <th className="num">{t('autobot.col.pnl')}</th>
                                        <th>{t('autobot.col.mode')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {managed.map((m, i) => {
                                        // Open position → show the honest "net if sold NOW" (gross move
                                        // minus BOTH fee sides, from the backend) with the fees underneath;
                                        // flat → the session's realized P&L.
                                        const open = num(m.openQty) > 0 && m.netIfSellNow != null;
                                        const pnl = open ? num(m.netIfSellNow) : num(m.pnl);
                                        return (
                                            <tr key={`${m.engine}-${m.symbol}-${i}`}>
                                                <td data-label={t('autobot.col.engine')}><span className={`gh-badge ${ENGINE_BADGE[m.engine] || 'gh-badge-muted'}`}>{m.engine}</span></td>
                                                <td data-label={t('autobot.col.symbol')}><strong>{m.symbol}</strong></td>
                                                <td data-label={t('autobot.col.entry')} className="num">{fmtPrice(m.openEntry)}</td>
                                                <td data-label={t('autobot.col.current')} className="num">{fmtPrice(m.currentPrice)}</td>
                                                <td data-label={t('autobot.col.pnl')} className={`num ${pnl >= 0 ? 'gh-pos' : 'gh-neg'}`}>
                                                    {fmtSigned(pnl)}
                                                    {open ? (
                                                        <small className="d-block text-muted">
                                                            {t('autobot.col.netNow')} · {t('autobot.col.fees')} {fmtMoney(num(m.entryFeePaid) + num(m.exitFeeNow))}
                                                        </small>
                                                    ) : null}
                                                </td>
                                                <td data-label={t('autobot.col.mode')}>
                                                    <span className={`gh-badge ${m.mode === 'LIVE' ? 'gh-badge-danger' : 'gh-badge-info'}`}>
                                                        {m.mode === 'LIVE' ? t('autobot.live') : t('autobot.paper')}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ---------- PLANNED NEXT ---------- */}
                <div className="card card-body border-0 shadow mb-4">
                    <small className="text-muted text-uppercase d-block mb-1">{t('autobot.plannedNext')}</small>
                    <p className="small text-muted mb-3">{t('autobot.plannedNext.disclaimer')}</p>
                    {planned.length === 0 ? (
                        <EmptyState title={t('autobot.plannedNext.empty')} text={t('autobot.plannedNext.emptyHint')} />
                    ) : (
                        <div className="row g-2">
                            {planned.map((p, i) => (
                                <div className="col-12 col-sm-6 col-lg-4" key={`${p.symbol}-${i}`}>
                                    <div className="card bg-dark text-white h-100 border-0 shadow-sm">
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <strong className="fs-6">{p.symbol}</strong>
                                                <span className="gh-badge gh-badge-info">{p.strategy}</span>
                                            </div>
                                            <div className="small text-gray-400">{p.reason}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {vetoes.length > 0 ? (
                        <div className="mt-3 pt-3 border-top border-secondary">
                            <small className="text-muted text-uppercase d-block mb-2">🚫 {t('autobot.vetoes')}</small>
                            <p className="small text-muted mb-2">{t('autobot.vetoes.hint')}</p>
                            {vetoes.slice(0, 5).map((v, i) => (
                                <div className="small mb-1" key={`${v.symbol}-${i}`}>
                                    <span className="gh-badge gh-badge-danger me-2">{v.symbol}</span>
                                    <span className="text-gray-400">{v.reason}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                {/* ---------- PERFORMANCE ---------- */}
                <div className="card card-body border-0 shadow mb-4">
                    <small className="text-muted text-uppercase d-block mb-3">{t('autobot.performance')}</small>
                    <div className="gh-grid mb-3">
                        <div>
                            <StatCard
                                label={t('autobot.perf.realized')}
                                value={<span className={`num ${num(overall.realizedPnl) >= 0 ? 'gh-pos' : 'gh-neg'}`}>{fmtSigned(num(overall.realizedPnl))}</span>}
                            />
                        </div>
                        <div>
                            <StatCard
                                label={t('autobot.perf.winRate')}
                                value={<span className="num">{fmtPct(overall.winRate)}%</span>}
                            />
                        </div>
                        <div>
                            <StatCard
                                label={t('autobot.perf.trades')}
                                value={<span className="num">{overall.trades != null ? overall.trades : 0}</span>}
                                sub={tv('autobot.perf.winsLosses', { w: overall.wins || 0, l: overall.losses || 0 })}
                            />
                        </div>
                        <div>
                            <StatCard
                                label={t('autobot.perf.activeBots')}
                                value={<span className="num">{overall.activeBots != null ? overall.activeBots : managed.length}</span>}
                            />
                        </div>
                    </div>
                    {equity.length > 0 ? (
                        <LineChart data={lineData} />
                    ) : (
                        <EmptyState title={t('autobot.perf.empty')} text={t('autobot.perf.emptyHint')} />
                    )}
                </div>

                {/* ---------- MARKET OUTLOOK ---------- */}
                <div className="mb-2">
                    <small className="text-muted text-uppercase d-block mb-1">{t('autobot.outlook')}</small>
                    <p className="small text-muted mb-3">{t('autobot.outlookHint')}</p>
                </div>
                <div className="row">
                    <AIInsights />
                    <SentimentWidget />
                </div>

                <ConfirmModal
                    open={confirmLive}
                    title={t('autobot.confirm.title')}
                    message={t('autobot.confirm.message')}
                    details={[
                        { label: t('autobot.mode'), value: t('autobot.live') },
                        { label: tv('autobot.budgetPerTrade', { ccy: baseCurrency }), value: `${fmtMoney(cfg && cfg.budgetPerTrade)} ${baseCurrency}` },
                        { label: t('autobot.maxTradesPerDay'), value: cfg ? cfg.maxTradesPerDay : '—' },
                        ...(cfg && cfg.useFutures ? [{ label: t('autobot.leverage'), value: `${cfg.leverage || 2}x` }] : [])
                    ]}
                    confirmLabel={t('autobot.confirm.turnOn')}
                    danger
                    onConfirm={doStart}
                    onCancel={() => setConfirmLive(false)}
                />

                <Toast type={notification.type} text={notification.text} />
                <Footer />
            </main>
        </React.Fragment>
    );
}

export default AutoBot;
